import { useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

export const useHorarios = () => {
  const [sucursales, setSucursales] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [horarioSeleccionado, setHorarioSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diasBase, setDiasBase] = useState([]);
  const [excepciones, setExcepciones] = useState([]);
  const [excepcionesCalendario, setExcepcionesCalendario] = useState([]);

  // Cargar sucursales
  const fetchSucursales = useCallback(async () => {
    try {
      const { data } = await api.get('/sucursales');
      setSucursales(data);
      const principal = data.find(s => s.es_principal) || data[0];
      if (principal) {
        setSucursalSeleccionada(principal.id);
      }
      return data;
    } catch (error) {
      toast.error('Error al cargar sucursales');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar horarios de una sucursal
  const fetchHorarios = useCallback(async (sucId) => {
    try {
      const { data } = await api.get(`/horarios/sucursal/${sucId}`);
      setHorarios(data);
      if (data.length > 0 && !horarioSeleccionado) {
        setHorarioSeleccionado(data[0].id);
      }
      return data;
    } catch (error) {
      toast.error('Error al cargar horarios');
      throw error;
    }
  }, [horarioSeleccionado]);

  // Cargar horario completo (con días base)
  const fetchHorarioCompleto = useCallback(async (horarioId) => {
    try {
      const { data } = await api.get(`/horarios/${horarioId}/completo`);
      setDiasBase(data.dias_base);
      return data;
    } catch (error) {
      toast.error('Error al cargar horario');
      throw error;
    }
  }, []);

  // Cargar excepciones de un mes
  const fetchExcepcionesMes = useCallback(async (horarioId, fecha) => {
    try {
      const year = fecha.getFullYear();
      const month = fecha.getMonth() + 1;
      const { data } = await api.get(`/horarios/${horarioId}/excepciones/mes/${year}/${month}`);
      setExcepciones(data);
      return data;
    } catch (error) {
      toast.error('Error al cargar excepciones');
      throw error;
    }
  }, []);

  // Crear horario
  const crearHorario = useCallback(async (formData) => {
    try {
      const { data } = await api.post('/horarios', formData);
      toast.success('Horario creado correctamente');
      await fetchHorarios(formData.sucursal_id);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear horario');
      throw error;
    }
  }, [fetchHorarios]);

  // Actualizar horario
  const actualizarHorario = useCallback(async (horarioId, formData) => {
    try {
      const { data } = await api.put(`/horarios/${horarioId}`, formData);
      toast.success('Horario actualizado correctamente');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar horario');
      throw error;
    }
  }, []);

  // Agregar excepción
  const agregarExcepcion = useCallback(async (horarioId, excepcionData) => {
    try {
      const { data } = await api.post(`/horarios/${horarioId}/excepciones`, excepcionData);
      toast.success('Excepción agregada correctamente');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al agregar excepción');
      throw error;
    }
  }, []);

  // Actualizar excepción
  const actualizarExcepcion = useCallback(async (excepcionId, excepcionData) => {
    try {
      const { data } = await api.put(`/horarios/excepciones/${excepcionId}`, excepcionData);
      toast.success('Excepción actualizada correctamente');
      return data;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar excepción');
      throw error;
    }
  }, []);

  // Eliminar excepción
  const eliminarExcepcion = useCallback(async (excepcionId) => {
    try {
      await api.delete(`/horarios/excepciones/${excepcionId}`);
      toast.success('Excepción eliminada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar excepción');
      throw error;
    }
  }, []);

  // Eliminar horario
  const eliminarHorario = useCallback(async (horarioId) => {
    try {
      await api.delete(`/horarios/${horarioId}`);
      toast.success('Horario eliminado correctamente');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar horario');
      throw error;
    }
  }, []);

  return {
    // State
    sucursales,
    horarios,
    sucursalSeleccionada,
    horarioSeleccionado,
    loading,
    diasBase,
    excepciones,
    excepcionesCalendario,

    // Setters
    setSucursalSeleccionada,
    setHorarioSeleccionado,
    setLoading,
    setDiasBase,
    setExcepciones,
    setExcepcionesCalendario,

    // Fetchers
    fetchSucursales,
    fetchHorarios,
    fetchHorarioCompleto,
    fetchExcepcionesMes,

    // CRUD
    crearHorario,
    actualizarHorario,
    agregarExcepcion,
    actualizarExcepcion,
    eliminarExcepcion,
    eliminarHorario
  };
};
